/**
 * Écran 0 — « Ce qui existe » (PRD §14.1) : le budget tendanciel expliqué simplement, en unités grand public
 * (€ par habitant, mois de salaire). Chiffres de l'année de base = baseline (ⓘ) ; « si rien ne change » = moteur
 * (programme vide, sans crise).
 */
import type { Figure } from "../../../engine/src/types.js";
import { figureNotes, srcButton } from "../components/source-info.js";
import { escapeHtml as e, eur, int, num, pct } from "../format.js";
import type { Store } from "../state.js";
import { outlookCard } from "../components/outlook-card.js";
import { plate } from "../components/brand.js";

const fv = (f: Figure | undefined): number | undefined => (f && f.value !== null ? f.value : undefined);

/** Année du bilan de fin de mandat (écran « Résultat ») : année suivant le dernier budget simulé. */
export function electionYear(store: Store): number {
  const b = store.content.baseline;
  const n = store.content.params.mandate_years?.value ?? b.asso.trend.length;
  return b.meta.base_year + n + 1;
}

/** Nombre d'années de budget du mandat (paramètre `mandate_years`, à défaut la longueur du tendanciel de la baseline). */
export function mandateYears(store: Store): number {
  const b = store.content.baseline;
  return store.content.params.mandate_years?.value ?? b.asso.trend.length;
}

/** Fil d'étapes : texte seul, étape courante soulignée, étapes passées dites « faites » aux lecteurs d'écran (pas de glyphe). */
export function stepsHtml(store: Store, active: 0 | 1 | 2 | 3 | 4): string {
  const names = ["Ce qui existe", "Programme", "Financement", "Mandat", `Résultat ${electionYear(store)}`];
  return `<nav aria-label="Étapes"><ol class="steps">${names
    .map((n, i) => (i === active ? `<li aria-current="step">${i + 1}. ${n}</li>` : `<li${i < active ? ' class="done"' : ""}>${i + 1}. ${n}${i < active ? '<span class="sr"> (fait)</span>' : ""}</li>`))
    .join("")}</ol></nav>`;
}

function src(store: Store, title: string, fig: Figure | undefined, path?: string, figureId?: string): string {
  const notes = figureNotes(fig);
  if (path && store.content.unsourcedPaths.some((p) => p.startsWith(path) || path.startsWith(p))) notes.unshift("Non sourcé : chiffre de la baseline de secours (prototype).");
  return srcButton({ title, sources: [fig?.source ?? "TODO"], notes, figureId }, { inline: true });
}

export function renderExiste(store: Store): string {
  const b = store.content.baseline;
  const y0 = b.meta.base_year;
  const pop = fv(b.macro.population);
  const medSal = fv(b.macro.median_net_salary_monthly);
  const perHead = (bn: number) => (pop ? eur(Math.round((bn * 1e9) / pop / 10) * 10) : undefined);
  const months = (bn: number) => (pop && medSal ? num((bn * 1e9) / pop / medSal, 1) : undefined);

  const revenues = b.state.revenues.filter((r) => fv(r.amount) !== undefined);
  const recTotal = revenues.reduce((a, r) => a + fv(r.amount)!, 0);
  const missions = b.state.missions.filter((m) => fv(m.credits) !== undefined).sort((a, c) => fv(c.credits)! - fv(a.credits)!);
  const missionsTotal = missions.reduce((a, m) => a + fv(m.credits)!, 0);
  const interest = fv(b.state.debt_interest);
  // Recettes v0.3 = comptabilité budgétaire 2026 : le solde comparable est le solde budgétaire 2026 (ADR 0014 §3), pas le solde
  // de l'État en comptabilité nationale 2025.
  const balFig = b.state.balance_budgetary_2026 && b.state.revenues === b.state.revenues_v03 ? b.state.balance_budgetary_2026 : b.state.balance;
  const balance = fv(balFig);
  const spendTotal = balance !== undefined ? recTotal - balance : undefined;
  const other = spendTotal !== undefined && interest !== undefined ? spendTotal - missionsTotal - interest : undefined;

  // Montants de dépenses et de recettes : barres à l'encre, toutes pareilles (neutralité, ADR 0018).
  const bars = (rows: Array<{ label: string; v: number; srcHtml: string }>, tot: number) =>
    rows
      .map((r) => `<div class="hb"><div class="hl"><span>${e(r.label)}${r.srcHtml}</span><b>${int(r.v)} Md€</b></div><span class="hbar"><i style="width:${Math.max(1, (r.v / tot) * 100).toFixed(1)}%"></i></span></div>`)
      .join("");

  const spendRows = [
    ...missions.slice(0, 6).map((m) => ({ label: m.label, v: fv(m.credits)!, srcHtml: src(store, m.label, m.credits, "state.missions", `baseline/state/missions_v03/${m.id}/credits`) })),
    ...(missions.length > 6 ? [{ label: "Autres missions", v: missions.slice(6).reduce((a, m) => a + fv(m.credits)!, 0), srcHtml: "" }] : []),
    ...(interest !== undefined ? [{ label: "Intérêts de la dette de l'État", v: interest, srcHtml: src(store, "Charge de la dette de l'État", b.state.debt_interest, "state.debt_interest", "baseline/state/debt_interest") }] : []),
    ...(other !== undefined && other > 0 ? [{ label: "Autres dépenses (dotations, Union européenne…)", v: other, srcHtml: "" }] : []),
  ];
  const recRows = revenues.map((r) => ({ label: r.label, v: fv(r.amount)!, srcHtml: src(store, r.label, r.amount, "state.revenues", `baseline/state/revenues_v03/${r.id}/amount`) }));

  // Toutes administrations publiques
  const apuDef = fv(b.apu.deficit_pct_gdp);
  const apuDebt = fv(b.apu.debt_bn);
  const apuDebtPct = fv(b.apu.debt_pct_gdp);
  const apuInt = fv(b.apu.interest_bn);
  const tr = store.trend;
  const last = tr.years[tr.years.length - 1];

  const big = (label: string, value: string, sub: string) => `<div class="kpi"><div class="l">${label}</div><div class="v">${value}</div><div class="e">${sub}</div></div>`;
  // Montants de l'État encore servis par la baseline de secours (prototype) : bandeau visible (red team v0.2 M7).
  const provisional = ["state.missions", "state.revenues", "state.balance"].filter((pth) => store.content.unsourcedPaths.some((u) => u === pth || u.startsWith(`${pth}.`) || u.startsWith(`${pth}[`)));
  const provBanner = provisional.length
    ? `<p class="pill warn prov">Montants provisoires (prototype) : ${provisional.map((x) => ({ "state.missions": "crédits des missions", "state.revenues": "recettes", "state.balance": "solde" })[x] ?? x).join(", ")} de l'État pas encore sourcés. Ne pas citer.</p>`
    : "";
  // Solde budgétaire de l'État publié (ex. PLF de l'année suivante, révisé) : affiché en attendant les missions sourcées.
  const sbKey = Object.keys(b.apu).find((k) => k === `state_budget_balance_${y0}`) ?? Object.keys(b.apu).find((k) => k.startsWith("state_budget_balance"));
  const sbFig = sbKey ? b.apu[sbKey] : undefined;
  const sb = fv(sbFig);
  const gapBn = balance !== undefined ? Math.abs(balance) : undefined;
  const balYear = balFig?.year;
  const sbLine =
    sb !== undefined && sbFig?.source !== "TODO" && balFig === b.state.balance
      ? `<p class="sm">Solde budgétaire de l'État ${sbFig?.year ?? y0} publié : <b>${sb < 0 ? "−" : "+"}${int(Math.abs(sb))} Md€</b>${src(store, "Solde budgétaire de l'État", sbFig)} <span class="mut">(comptabilité budgétaire, non comparable terme à terme au chiffre ci-dessus)</span>.</p>`
      : "";
  const stateBlock =
    spendTotal !== undefined && balance !== undefined
      ? `${provBanner}<div class="pub">
        ${big(`L'État encaisse${src(store, "Recettes de l'État", revenues[0]?.amount, "state.revenues")}`, `${int(recTotal)} Md€`, `${perHead(recTotal) ? `≈ ${perHead(recTotal)} par habitant` : ""}${provisional.includes("state.revenues") ? " · provisoire" : ""}`)}
        ${big(`L'État dépense${src(store, "Solde de l'État", balFig, "state.balance", balFig === b.state.balance ? "baseline/state/balance" : "baseline/state/balance_budgetary_2026")}`, `${int(spendTotal)} Md€`, `${perHead(spendTotal) ? `≈ ${perHead(spendTotal)} par habitant` : ""}${provisional.length ? " · provisoire" : ""}`)}
      </div>
      ${
        gapBn !== undefined
          ? plate(
              `Il manque chaque année à l'État${balYear !== undefined ? ` (${balYear})` : ""}${src(store, "Solde de l'État", balFig, "state.balance", balFig === b.state.balance ? "baseline/state/balance" : "baseline/state/balance_budgetary_2026")}`,
              `${int(gapBn)} Md€`,
              { note: `${perHead(gapBn) ? `Soit ${perHead(gapBn)} par habitant${months(gapBn) ? ` (≈ ${months(gapBn)} mois de salaire médian)` : ""}. ` : ""}L'État les emprunte.` },
            )
          : ""
      }
      ${sbLine}`
      : provBanner;

  const apuBlock = `<div class="pub">
    ${apuDef !== undefined ? big(`Déficit public ${b.apu.deficit_pct_gdp?.year ?? y0}${src(store, "Déficit public", b.apu.deficit_pct_gdp, undefined, "baseline/apu/deficit_pct_gdp")}`, `${pct(Math.abs(apuDef))} du PIB`, "État + Sécurité sociale + collectivités") : ""}
    ${apuDebt !== undefined ? big(`Dette publique ${b.apu.debt_bn?.year ?? y0}${src(store, "Dette publique", b.apu.debt_bn, undefined, "baseline/apu/debt_bn")}`, `${int(apuDebt)} Md€`, `${apuDebtPct !== undefined ? `${pct(apuDebtPct, 0)} du PIB` : ""}${perHead(apuDebt) ? ` · ≈ ${perHead(apuDebt)} par habitant` : ""}${months(apuDebt) ? ` · ${months(apuDebt)} mois de salaire` : ""}`) : ""}
    ${apuInt !== undefined ? big(`Intérêts payés${src(store, "Charge d'intérêts des administrations publiques", b.apu.interest_bn, undefined, "baseline/apu/interest_bn")}`, `${int(apuInt)} Md€/an`, perHead(apuInt) ? `≈ ${perHead(apuInt)} par habitant` : "") : ""}
    ${pop ? big(`Population${src(store, "Population", b.macro.population, undefined, "baseline/macro/population")}`, `${num(pop / 1e6, 1)} millions`, "pour ramener les milliards à chacun") : ""}
  </div>`;

  const trendBlock = last
    ? `<div class="card"><h2>Si rien ne change d'ici ${last.year}</h2>
      <p class="mut sm">Le budget tendanciel, simulé par le moteur : aucune mesure nouvelle, aucune crise. C'est le point de comparaison de toute la partie.</p>
      <div class="pub">
        ${last.kpis.deficit_pct_gdp !== undefined ? big("Déficit public", `${pct(Math.abs(last.kpis.deficit_pct_gdp))} du PIB`, apuDef !== undefined ? `${pct(Math.abs(apuDef))} en ${b.apu.deficit_pct_gdp?.year ?? y0}` : "") : ""}
        ${last.kpis.debt_pct_gdp !== undefined ? big("Dette publique", `${pct(last.kpis.debt_pct_gdp, 0)} du PIB`, apuDebtPct !== undefined ? `${pct(apuDebtPct, 0)} en ${b.apu.debt_pct_gdp?.year ?? y0}` : "") : ""}
        ${last.kpis.interest_bn !== undefined ? big("Intérêts", `${int(last.kpis.interest_bn)} Md€/an`, apuInt !== undefined ? `${int(apuInt)} Md€ en ${b.apu.interest_bn?.year ?? y0}` : "") : ""}
        ${last.kpis.unemployment_pct !== undefined ? big("Chômage", pct(last.kpis.unemployment_pct), "") : ""}
      </div>
      <p class="hint">Simulation ${e(store.engine.name)} ; chaque hypothèse du moteur est un paramètre sourcé ou marqué « à sourcer ».</p></div>`
    : "";

  return `<div class="card">
    ${stepsHtml(store, 0)}
    <h1>Le budget de la France en ${y0}</h1>
    <p class="mut">Avant de jouer, voici ce qui existe : ce que l'État dépense, ce qu'il encaisse, ce que coûte déjà la dette. Touche « i » à côté d'un chiffre pour voir sa source.</p>
    ${stateBlock}
  </div>
  ${spendRows.length ? `<div class="card"><h2>Où va l'argent de l'État</h2>${bars(spendRows, Math.max(...spendRows.map((r) => r.v)))}<p class="hint">Crédits des missions du budget général (Md€ courants). La Sécurité sociale (retraites, santé) a son propre budget.</p></div>` : ""}
  ${recRows.length ? `<div class="card"><h2>D'où il vient</h2>${bars(recRows, Math.max(...recRows.map((r) => r.v)))}</div>` : ""}
  <div class="card"><h2>Toutes administrations publiques</h2>${apuBlock}</div>
  ${trendBlock}
  ${outlookCard(store, { trend: tr.years, title: "Le tendanciel du jeu face à la trajectoire du gouvernement" })}`;
}

export function renderExisteBottom(store: Store): string {
  return `<div class="in"><p class="sm mut">À toi de jouer : ${int(mandateYears(store))} ans de mandat.</p><button type="button" class="primary" data-action="go-programme">Composer mon programme</button></div>`;
}
